import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Column, Grid, GridItem, Row, TextButton, TextField, Tooltip, Typography, USPhoneNumberInput } from 'seagull';
import { CloseCross, Info } from 'seagull/icons';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './ContactForm.module.scss';
import { buildContactValidators, GetContactFormTID } from './ContactForm.utils';
export const contactFormTextsPath = 'components.contactForm';
const PhoneInfoAnchor = /*#__PURE__*/ forwardRef((_0, _1)=>{
    let _ref = [
        _0,
        _1
    ], [_ref1, ..._rest] = _ref, { children } = _ref1, rest = _object_without_properties(_ref1, [
        "children"
    ]), [ref] = _rest;
    return(// eslint-disable-next-line r15/forbid-elements -- MUI Tooltip anchors its popper to a ref-forwarding DOM child and injects hover handlers via props; seagull Box drops both
    /*#__PURE__*/ _jsx("span", _object_spread_props(_object_spread({
        ref: ref
    }, rest), {
        children: children
    })));
});
export function ContactForm(param) {
    let { namePrefix, title, lockedClientName, accountingMapping, showRemove = false, onRemove, validators: validatorsOverride, testId = 'contact-form' } = param;
    const { t } = useLocaleKeys(contactFormTextsPath);
    const { control } = useFormContext();
    const { ContactFormTestIds } = useMemo(()=>GetContactFormTID(testId), [
        testId
    ]);
    const defaultValidators = useMemo(()=>buildContactValidators({
            firstNameRequiredError: t.firstNameRequiredError(),
            lastNameRequiredError: t.lastNameRequiredError(),
            emailInvalidError: t.emailInvalidError(),
            phoneInvalidError: t.phoneInvalidError()
        }), [
        t
    ]);
    const validators = validatorsOverride !== null && validatorsOverride !== void 0 ? validatorsOverride : defaultValidators;
    const phoneInfoTooltip = /*#__PURE__*/ _jsx(Tooltip, {
        placement: "top",
        title: t.phoneTooltip(),
        testId: ContactFormTestIds.PhoneInfoTooltip(),
        children: /*#__PURE__*/ _jsx(PhoneInfoAnchor, {
            "data-testid": ContactFormTestIds.PhoneInfoAnchor(),
            children: /*#__PURE__*/ _jsx(Info, {})
        })
    });
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        className: styles.container,
        testId: ContactFormTestIds.Root(),
        children: [
            (title != null || showRemove) && /*#__PURE__*/ _jsxs(Row, {
                justifyContent: "space-between",
                alignItems: "center",
                children: [
                    title != null && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        weight: "normal",
                        testId: ContactFormTestIds.Title(),
                        children: title
                    }),
                    showRemove && /*#__PURE__*/ _jsx(TextButton, {
                        variant: "paragraph2",
                        skin: "primary",
                        weight: "normal",
                        underline: "none",
                        prefixIcon: /*#__PURE__*/ _jsx(CloseCross, {
                            size: 16
                        }),
                        onClick: onRemove,
                        testId: ContactFormTestIds.Remove(),
                        bi: {
                            name: 'remove_contact'
                        },
                        children: t.removeAction()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Grid, {
                rowGap: "medium",
                children: [
                    lockedClientName != null && /*#__PURE__*/ _jsx(GridItem, {
                        span: 12,
                        children: /*#__PURE__*/ _jsx(TextField, {
                            value: lockedClientName,
                            onChange: ()=>{},
                            label: t.clientNameLabel(),
                            testId: ContactFormTestIds.ClientName(),
                            bi: {
                                name: 'client_name'
                            },
                            disabled: true
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: 6,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "".concat(namePrefix, ".firstName"),
                            rules: {
                                validate: validators.firstName
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps)), {
                                    label: t.firstNameLabel(),
                                    testId: ContactFormTestIds.FirstName(),
                                    bi: {
                                        name: 'first_name'
                                    }
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: 6,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "".concat(namePrefix, ".lastName"),
                            rules: {
                                validate: validators.lastName
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps)), {
                                    label: t.lastNameLabel(),
                                    testId: ContactFormTestIds.LastName(),
                                    bi: {
                                        name: 'last_name'
                                    }
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: 12,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "".concat(namePrefix, ".email"),
                            rules: {
                                validate: validators.email
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps)), {
                                    label: t.emailLabel(),
                                    testId: ContactFormTestIds.Email(),
                                    bi: {
                                        name: 'email_address'
                                    }
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: 12,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "".concat(namePrefix, ".phone"),
                            rules: {
                                validate: validators.phone
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps)), {
                                    label: t.phoneLabel(),
                                    testId: ContactFormTestIds.Phone(),
                                    suffix: phoneInfoTooltip,
                                    bi: {
                                        name: 'phone_number'
                                    }
                                }))
                        })
                    }),
                    accountingMapping != null && /*#__PURE__*/ _jsx(GridItem, {
                        span: 12,
                        children: /*#__PURE__*/ _jsx(Column, {
                            width: "100%",
                            alignItems: "stretch",
                            testId: ContactFormTestIds.AccountingMapping(),
                            children: accountingMapping
                        })
                    })
                ]
            })
        ]
    });
}
